/**
 * render/iso-engine.ts — Hearth Isometric Material Rendering Engine
 *
 * A declarative rendering engine for pipeline-generated isometric assets.
 * Assets are pure data (IsoAssetDef); this engine handles ALL rendering:
 *
 *   - Per-face gradient fills computed from PBR material properties
 *   - Material-type grain patterns (metal, wood, leather, cloth, stone)
 *   - Specular highlights via radial gradients (metalness-driven)
 *   - Edge detail: AO shadow, rim highlight, silhouette, face division
 *   - Direction rotation (4-way: SE/SW/NW/NE) with face color mirroring
 *   - Runtime colorization via state.tint (multiply composite)
 *   - LOD: skip grain + specular at small draw scale
 *   - Emissive glow with time-based pulse
 *
 * Novel: 3D models compile to ~30-line data descriptors. All visual
 * intelligence lives here, improving every asset when the engine improves.
 */

// ─── Asset Definition Types ─────────────────────────────────────────────────

export interface IsoAssetDef {
  id: string;
  name: string;
  tile: [number, number];
  geometry: BoxGeometry;
  faces: {
    top: FaceMaterial;
    left: FaceMaterial;
    right: FaceMaterial;
  };
  anchors: AnchorDef[];
}

export interface BoxGeometry {
  w: number;   // iso east-axis pixels
  d: number;   // iso south-axis pixels
  h: number;   // iso height pixels
}

export interface FaceMaterial {
  base: string;           // hex base color
  palette: string[];      // frequency-sorted dominant colors
  metalness: number;      // 0-1
  roughness: number;      // 0-1
  opacity: number;        // 0-1
  emissive?: string;      // hex glow color
  materialType: MaterialType;
  /** Detected gradient (from texture analysis) — overrides synthetic gradient */
  gradient?: { stops: [number, string][]; strength: number };
  /** Detected grain parameters */
  grain?: { scale: number; contrast: number; angle?: number };
  /** Detected specular highlight position/intensity */
  specular?: { x: number; y: number; radius: number; intensity: number };
  /** Edge treatment intensities (from depth discontinuity analysis) */
  edgeDarken?: number;    // 0-1, bottom edge AO intensity
  edgeHighlight?: number; // 0-1, top edge rim light intensity
}

export type MaterialType = "metal" | "leather" | "cloth" | "wood" | "stone" | "crystal" | "organic" | "flat";

export interface AnchorDef {
  type: "surface_slot" | "sit_point" | "use_point";
  wx: number;
  wy: number;
  elevation: number;
  dir: "N" | "S" | "E" | "W";
}

// ─── Render Function ────────────────────────────────────────────────────────

/**
 * Render an isometric asset from its declarative definition.
 * This is the single entry point — all material rendering logic lives here.
 */
export function renderIsoAsset(
  ctx: CanvasRenderingContext2D,
  def: IsoAssetDef,
  screenX: number,
  screenY: number,
  direction: 0 | 1 | 2 | 3,
  state: Record<string, unknown>,
  time: number,
): void {
  const sx = screenX;
  const sy = screenY + 16;
  const { w: bw, d: bd, h } = def.geometry;

  // Direction-aware: swap w/d for 90° rotations, mirror left↔right faces
  const swap = direction === 1 || direction === 3;
  const mirror = direction === 1 || direction === 2;
  const w = swap ? bd : bw;
  const d = swap ? bw : bd;
  const ty = sy - h;

  // Resolve face materials (mirror swaps left/right)
  const topMat = def.faces.top;
  const leftMat = mirror ? def.faces.right : def.faces.left;
  const rightMat = mirror ? def.faces.left : def.faces.right;

  // LOD: smooth detail scaling from current transform
  const xform = ctx.getTransform();
  const scale = Math.sqrt(xform.a * xform.a + xform.b * xform.b);
  const lod2 = scale > 0.7;                          // full detail
  const lod1 = scale > 0.35;                          // reduced detail
  const grainFade = lod1 ? Math.min(1, (scale - 0.35) / 0.35) : 0; // smooth fadeout

  // ═══════════════════════════════════════════════════════════════════════════
  // PASS 1: Palette-driven gradient base fills (respects per-face opacity)
  // ═══════════════════════════════════════════════════════════════════════════
  for (const [face, mat] of [["top", topMat], ["left", leftMat], ["right", rightMat]] as const) {
    if (mat.opacity < 1) ctx.globalAlpha = mat.opacity;
    fillFace(ctx, face, sx, sy, w, d, h, ty, mat);
    if (mat.opacity < 1) ctx.globalAlpha = 1;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PASS 1.5: Contact shadows (darkening where faces meet)
  // ═══════════════════════════════════════════════════════════════════════════
  renderContactShadow(ctx, sx, sy, w, d, h, ty);

  // ═══════════════════════════════════════════════════════════════════════════
  // PASS 1.6: Colorization tint
  // ═══════════════════════════════════════════════════════════════════════════
  if (state.tint) {
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = state.tint as string;
    traceFace(ctx, "top", sx, sy, w, d, h, ty); ctx.fill();
    traceFace(ctx, "left", sx, sy, w, d, h, ty); ctx.fill();
    traceFace(ctx, "right", sx, sy, w, d, h, ty); ctx.fill();
    ctx.globalCompositeOperation = "source-over";
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PASS 2: Surface grain (smooth LOD fadeout)
  // ═══════════════════════════════════════════════════════════════════════════
  if (grainFade > 0) {
    renderGrain(ctx, sx, sy, w, d, h, ty, topMat, "top", grainFade);
    renderGrain(ctx, sx, sy, w, d, h, ty, leftMat, "left", grainFade);
    renderGrain(ctx, sx, sy, w, d, h, ty, rightMat, "right", grainFade);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PASS 3: Specular highlights (LOD-gated, uses detected positions)
  // ═══════════════════════════════════════════════════════════════════════════
  if (lod1 && (topMat.metalness > 0.1 || topMat.specular || rightMat.metalness > 0.1 || rightMat.specular)) {
    renderSpecular(ctx, sx, sy, w, d, h, ty, topMat, rightMat);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PASS 4: Edge detail + fresnel rim
  // ═══════════════════════════════════════════════════════════════════════════
  renderEdges(ctx, sx, sy, w, d, h, ty, topMat, leftMat, rightMat);

  // ═══════════════════════════════════════════════════════════════════════════
  // PASS 5: Emissive glow
  // ═══════════════════════════════════════════════════════════════════════════
  // Per-face emissive: only faces with emissive property get the glow
  const hasEmissive = topMat.emissive || leftMat.emissive || rightMat.emissive;
  if (hasEmissive) {
    const glowAlpha = 0.15 + Math.sin(time * 2.0) * 0.08;
    for (const [face, mat] of [["top", topMat], ["left", leftMat], ["right", rightMat]] as const) {
      if (mat.emissive) {
        ctx.globalAlpha = glowAlpha;
        ctx.fillStyle = mat.emissive;
        traceFace(ctx, face, sx, sy, w, d, h, ty); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }
}

// ─── Face Path Tracing ──────────────────────────────────────────────────────

function traceFace(
  ctx: CanvasRenderingContext2D,
  face: "top" | "left" | "right",
  sx: number, sy: number, w: number, d: number, h: number, ty: number,
): void {
  ctx.beginPath();
  if (face === "top") {
    ctx.moveTo(sx, ty);
    ctx.lineTo(sx + w, ty - w * 0.5);
    ctx.lineTo(sx + w - d, ty - w * 0.5 - d * 0.5);
    ctx.lineTo(sx - d, ty - d * 0.5);
  } else if (face === "left") {
    ctx.moveTo(sx, ty);
    ctx.lineTo(sx - d, ty - d * 0.5);
    ctx.lineTo(sx - d, sy - d * 0.5);
    ctx.lineTo(sx, sy);
  } else {
    ctx.moveTo(sx, ty);
    ctx.lineTo(sx + w, ty - w * 0.5);
    ctx.lineTo(sx + w, sy - w * 0.5);
    ctx.lineTo(sx, sy);
  }
  ctx.closePath();
}

// ─── Gradient Face Fill ─────────────────────────────────────────────────────

function fillFace(
  ctx: CanvasRenderingContext2D,
  face: "top" | "left" | "right",
  sx: number, sy: number, w: number, d: number, h: number, ty: number,
  mat: FaceMaterial,
): void {
  const base = mat.base;
  let g: CanvasGradient;
  if (face === "top") {
    g = ctx.createLinearGradient(sx - d, ty - d * 0.5, sx + w, ty - w * 0.5);
  } else {
    g = ctx.createLinearGradient(sx, ty, sx, sy);
  }

  if (mat.gradient && mat.gradient.stops.length >= 2) {
    // Use detected gradient stops, modulated by gradient.strength:
    // strength=1.0 → pure detected colors; strength=0.1 → blend heavily toward base
    const str = mat.gradient.strength;
    for (const [offset, color] of mat.gradient.stops) {
      g.addColorStop(offset, str > 0.5 ? color : lerpColor(base, color, str * 2));
    }
  } else if (mat.palette.length >= 3) {
    // Multi-stop gradient from palette colors (blend in linear space)
    g.addColorStop(0, shade(mat.palette[0], 1.04));
    g.addColorStop(0.5, mat.palette[1]);
    g.addColorStop(1, shade(mat.palette[Math.min(2, mat.palette.length - 1)], 0.96));
  } else if (mat.palette.length === 2) {
    // Two-color gradient from palette
    g.addColorStop(0, mat.palette[0]);
    g.addColorStop(1, mat.palette[1]);
  } else {
    // Synthetic gradient from PBR: metallic = more contrast
    const contrast = 0.06 + mat.metalness * 0.08;
    g.addColorStop(0, shade(base, 1 + contrast));
    g.addColorStop(0.5, base);
    g.addColorStop(1, shade(base, 1 - contrast - 0.02));
  }

  ctx.fillStyle = g;
  traceFace(ctx, face, sx, sy, w, d, h, ty);
  ctx.fill();
}

// ─── Surface Grain ──────────────────────────────────────────────────────────

function renderGrain(
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number, w: number, d: number, h: number, ty: number,
  mat: FaceMaterial,
  face: "top" | "left" | "right",
  lodFade: number = 1,
): void {
  const { materialType: type, roughness } = mat;
  if (type === "flat" && roughness < 0.3) return;

  // Use detected grain contrast if available, otherwise derive from roughness
  const detectedContrast = mat.grain?.contrast;
  const alpha = Math.min(0.15, detectedContrast ?? (roughness * 0.08 + 0.02)) * lodFade;
  if (alpha < 0.005) return;

  ctx.save();
  traceFace(ctx, face, sx, sy, w, d, h, ty);
  ctx.clip();

  switch (type) {
    case "metal": {
      // Brushed metal: thin strokes — density from grain.scale, direction from grain.angle
      const grainScale = mat.grain?.scale ?? Math.max(3, 8 - roughness * 6);
      const isHorizontal = mat.grain?.angle !== undefined ? mat.grain.angle >= 45 : true;
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = "rgba(255,255,255,0.3)";
      ctx.lineWidth = 0.5;
      if (face === "left") {
        for (let i = 0; i < h; i += grainScale) {
          ctx.beginPath();
          if (isHorizontal) {
            ctx.moveTo(sx - d, sy - i - d * 0.5);
            ctx.lineTo(sx, sy - i);
          } else {
            ctx.moveTo(sx - d * (i / h), ty - d * 0.5 * (i / h));
            ctx.lineTo(sx - d * (i / h), sy - d * 0.5 * (i / h));
          }
          ctx.stroke();
        }
      } else if (face === "right") {
        for (let i = 0; i < h; i += grainScale) {
          ctx.beginPath();
          if (isHorizontal) {
            ctx.moveTo(sx, sy - i);
            ctx.lineTo(sx + w, sy - i - w * 0.5);
          } else {
            ctx.moveTo(sx + w * (i / h), ty - w * 0.5 * (i / h));
            ctx.lineTo(sx + w * (i / h), sy - w * 0.5 * (i / h));
          }
          ctx.stroke();
        }
      }
      break;
    }
    case "wood": {
      // Wavy grain lines — spacing from grain.scale
      const woodScale = mat.grain?.scale ?? 4;
      ctx.globalAlpha = alpha * 1.5;
      ctx.strokeStyle = "rgba(80,50,20,0.15)";
      ctx.lineWidth = 0.7;
      for (let i = 0; i < h; i += woodScale) {
        ctx.beginPath();
        const y = sy - i;
        ctx.moveTo(sx - d, y - d * 0.5);
        ctx.quadraticCurveTo(sx - d * 0.5, y - d * 0.25 + Math.sin(i * 0.5) * 1.5, sx, y);
        ctx.stroke();
      }
      break;
    }
    case "stone": {
      // Stipple noise dots — count from grain.scale (lower scale = more dots)
      const stoneCount = Math.round(20 / (mat.grain?.scale ?? 4));
      ctx.globalAlpha = alpha * 2;
      for (let i = 0; i < stoneCount; i++) {
        const px = sx + (Math.sin(i * 7.3 + 0.5) * w * 0.3) - d * 0.2;
        const py = ty + (sy - ty) * (Math.sin(i * 2.3 + 1.1) * 0.5 + 0.5);
        ctx.fillStyle = i % 2 ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.04)";
        ctx.beginPath();
        ctx.arc(px, py - d * 0.15, 1.5 + Math.sin(i * 4.1) * 0.8, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case "leather": {
      // Subtle pore dots — density from grain.scale
      const leatherCount = Math.round(25 / (mat.grain?.scale ?? 5));
      ctx.globalAlpha = alpha * 1.5;
      ctx.fillStyle = "rgba(0,0,0,0.06)";
      for (let i = 0; i < leatherCount; i++) {
        const px = sx + (Math.sin(i * 3.7 + face.length) - 0.5) * w * 0.4;
        const py = ty + (sy - ty) * (Math.cos(i * 2.1) * 0.5 + 0.5);
        ctx.beginPath();
        ctx.arc(px - d * 0.2, py - d * 0.15, 0.8, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case "cloth": {
      // Crosshatch weave — spacing from grain.scale
      const clothScale = mat.grain?.scale ?? 4;
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = "rgba(0,0,0,0.04)";
      ctx.lineWidth = 0.3;
      for (let i = -h; i < d + h; i += clothScale) {
        ctx.beginPath();
        ctx.moveTo(sx - i, sy - h + i * 0.5);
        ctx.lineTo(sx - i + 8, sy - h + i * 0.5 - 4);
        ctx.stroke();
      }
      break;
    }
    case "crystal": {
      // Faceted crystalline shards — angular highlight lines at varying angles
      const crystalScale = mat.grain?.scale ?? 5;
      const shardCount = Math.round(18 / crystalScale);
      ctx.globalAlpha = alpha * 2;
      ctx.lineWidth = 0.6;
      for (let i = 0; i < shardCount; i++) {
        const px = sx + Math.sin(i * 4.7 + 0.3) * w * 0.35 - d * 0.2;
        const py = ty + (sy - ty) * (Math.sin(i * 2.9 + 1.7) * 0.4 + 0.5);
        const angle = i * 1.2 + Math.sin(i * 3.1) * 0.8;
        const len = 3 + Math.sin(i * 5.3) * 2;
        // Alternating bright/refractive lines
        ctx.strokeStyle = i % 3 === 0 ? "rgba(255,255,255,0.2)" : "rgba(200,220,255,0.1)";
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(px + Math.cos(angle) * len, py + Math.sin(angle) * len * 0.5);
        ctx.stroke();
      }
      break;
    }
    case "organic": {
      // Irregular cellular blobs — soft organic pores/bumps
      const organicScale = mat.grain?.scale ?? 4;
      const cellCount = Math.round(22 / organicScale);
      ctx.globalAlpha = alpha * 1.2;
      for (let i = 0; i < cellCount; i++) {
        const px = sx + (Math.sin(i * 5.3 + face.length * 1.7) - 0.4) * w * 0.4 - d * 0.15;
        const py = ty + (sy - ty) * (Math.cos(i * 2.7 + 0.3) * 0.45 + 0.5);
        const r = 1.0 + Math.sin(i * 3.9) * 0.6;
        ctx.fillStyle = i % 3 === 0 ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.03)";
        ctx.beginPath();
        ctx.ellipse(px, py, r * 1.3, r, Math.sin(i * 2.1) * 0.5, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    default: {
      // Generic subtle noise for rough surfaces
      if (roughness > 0.3) {
        ctx.globalAlpha = alpha;
        for (let i = 0; i < 8; i++) {
          const px = sx + Math.sin(i * 5.1) * w * 0.3 - d * 0.2;
          const py = ty + (sy - ty) * (Math.cos(i * 3.7) * 0.4 + 0.5);
          ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)";
          ctx.beginPath();
          ctx.arc(px, py, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      break;
    }
  }

  ctx.restore();
}

// ─── Specular Highlights ────────────────────────────────────────────────────

function renderSpecular(
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number, w: number, d: number, h: number, ty: number,
  topMat: FaceMaterial,
  rightMat: FaceMaterial,
): void {
  // Top face: overhead specular — use detected position if available
  const topSpec = topMat.specular;
  const topPeak = topSpec?.intensity ?? (topMat.metalness * 0.35 + 0.08);
  if (topPeak > 0.05) {
    ctx.save();
    traceFace(ctx, "top", sx, sy, w, d, h, ty);
    ctx.clip();
    const specX = topSpec?.x ?? 0.35;
    const specY = topSpec?.y ?? 0.35;
    const specR = topSpec?.radius ?? (0.3 + topMat.metalness * 0.3);
    const cx = sx + w * specX - d * (1 - specX);
    const cy = ty - w * specX * 0.5 - d * (1 - specX) * 0.5;
    const r = Math.max(w, d) * specR;
    const sg = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    sg.addColorStop(0, `rgba(255,255,255,${topPeak.toFixed(2)})`);
    sg.addColorStop(0.5, `rgba(255,255,255,${(topPeak * 0.2).toFixed(2)})`);
    sg.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = sg;
    ctx.fill();
    ctx.restore();
  }

  // Right face: edge catch light — use detected or derive from metalness
  const rightSpec = rightMat.specular;
  const rightIntensity = rightSpec?.intensity ?? (rightMat.metalness > 0.2 ? rightMat.metalness * 0.15 : 0);
  if (rightIntensity > 0.03) {
    ctx.save();
    traceFace(ctx, "right", sx, sy, w, d, h, ty);
    ctx.clip();
    const rg = ctx.createLinearGradient(sx + w, ty - w * 0.5, sx, sy);
    rg.addColorStop(0, `rgba(255,255,255,${rightIntensity.toFixed(2)})`);
    rg.addColorStop(0.4, "rgba(255,255,255,0)");
    ctx.fillStyle = rg;
    ctx.fill();
    ctx.restore();
  }
}

// ─── Edge Detail ────────────────────────────────────────────────────────────

function renderEdges(
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number, w: number, d: number, h: number, ty: number,
  topMat: FaceMaterial,
  leftMat: FaceMaterial,
  rightMat: FaceMaterial,
): void {
  // Use detected edge intensities or derive from PBR
  const edgeHL = topMat.edgeHighlight ?? (topMat.metalness > 0.3 ? 0.22 : 0.12);
  const edgeAO = topMat.edgeDarken ?? 0.2;

  // Top edge rim highlights
  ctx.strokeStyle = `rgba(255,255,255,${edgeHL.toFixed(2)})`;
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(sx, ty);
  ctx.lineTo(sx + w, ty - w * 0.5);
  ctx.moveTo(sx, ty);
  ctx.lineTo(sx - d, ty - d * 0.5);
  ctx.stroke();

  // Bottom edge AO shadow
  ctx.strokeStyle = `rgba(0,0,0,${edgeAO.toFixed(2)})`;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(sx + w, sy - w * 0.5);
  ctx.moveTo(sx, sy);
  ctx.lineTo(sx - d, sy - d * 0.5);
  ctx.stroke();

  // Center vertical edge (face division)
  ctx.strokeStyle = "rgba(0,0,0,0.08)";
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(sx, ty);
  ctx.lineTo(sx, sy);
  ctx.stroke();

  // Outer silhouette
  ctx.strokeStyle = "rgba(0,0,0,0.12)";
  ctx.lineWidth = 0.7;
  ctx.beginPath();
  ctx.moveTo(sx + w, ty - w * 0.5);
  ctx.lineTo(sx + w, sy - w * 0.5);
  ctx.moveTo(sx - d, ty - d * 0.5);
  ctx.lineTo(sx - d, sy - d * 0.5);
  ctx.moveTo(sx + w - d, ty - w * 0.5 - d * 0.5);
  ctx.lineTo(sx - d, ty - d * 0.5);
  ctx.moveTo(sx + w - d, ty - w * 0.5 - d * 0.5);
  ctx.lineTo(sx + w, ty - w * 0.5);
  ctx.stroke();

  // Fresnel rim: view-dependent edge brightening on outer silhouette
  // Strongest on smooth/metallic surfaces, simulates light wrapping around edges
  const fresnelAlpha = 0.04 + Math.max(leftMat.metalness, rightMat.metalness) * 0.08;
  if (fresnelAlpha > 0.03) {
    ctx.save();
    // Left outer edge fresnel
    traceFace(ctx, "left", sx, sy, w, d, h, ty);
    ctx.clip();
    const lg = ctx.createLinearGradient(sx, ty, sx - d, ty - d * 0.5);
    lg.addColorStop(0, "rgba(255,255,255,0)");
    lg.addColorStop(0.7, "rgba(255,255,255,0)");
    lg.addColorStop(1, `rgba(255,255,255,${fresnelAlpha.toFixed(2)})`);
    ctx.fillStyle = lg;
    ctx.fill();
    ctx.restore();

    ctx.save();
    // Right outer edge fresnel
    traceFace(ctx, "right", sx, sy, w, d, h, ty);
    ctx.clip();
    const rg = ctx.createLinearGradient(sx, ty, sx + w, ty - w * 0.5);
    rg.addColorStop(0, "rgba(255,255,255,0)");
    rg.addColorStop(0.7, "rgba(255,255,255,0)");
    rg.addColorStop(1, `rgba(255,255,255,${fresnelAlpha.toFixed(2)})`);
    ctx.fillStyle = rg;
    ctx.fill();
    ctx.restore();
  }
}

// ─── Contact Shadow ────────────────────────────────────────────────────────

function renderContactShadow(
  ctx: CanvasRenderingContext2D,
  sx: number, sy: number, w: number, d: number, h: number, ty: number,
): void {
  // Subtle darkening where side faces meet the top face (ambient occlusion)
  ctx.save();

  // Left face top-edge contact shadow
  traceFace(ctx, "left", sx, sy, w, d, h, ty);
  ctx.clip();
  const lcs = ctx.createLinearGradient(sx, ty, sx, ty + h * 0.15);
  lcs.addColorStop(0, "rgba(0,0,0,0.06)");
  lcs.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = lcs;
  ctx.fill();
  ctx.restore();

  ctx.save();
  // Right face top-edge contact shadow
  traceFace(ctx, "right", sx, sy, w, d, h, ty);
  ctx.clip();
  const rcs = ctx.createLinearGradient(sx, ty, sx, ty + h * 0.15);
  rcs.addColorStop(0, "rgba(0,0,0,0.06)");
  rcs.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = rcs;
  ctx.fill();
  ctx.restore();
}

// ─── Color Utility ──────────────────────────────────────────────────────────

// sRGB ↔ linear conversion for perceptually correct color math
const toLinear = (v: number) => v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
const toSRGB = (v: number) => v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;

function shade(hex: string, factor: number): string {
  const n = parseInt(hex.replace("#", ""), 16);
  const cl = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  // Decode sRGB → linear, multiply, encode linear → sRGB
  const r = cl(toSRGB(toLinear(((n >> 16) & 255) / 255) * factor) * 255);
  const g = cl(toSRGB(toLinear(((n >> 8) & 255) / 255) * factor) * 255);
  const b = cl(toSRGB(toLinear((n & 255) / 255) * factor) * 255);
  return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}

/** Blend two hex colors in linear space */
function lerpColor(a: string, b: string, t: number): string {
  const na = parseInt(a.replace("#", ""), 16);
  const nb = parseInt(b.replace("#", ""), 16);
  const cl = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  const mix = (ca: number, cb: number) =>
    toSRGB(toLinear(ca / 255) * (1 - t) + toLinear(cb / 255) * t) * 255;
  const r = cl(mix((na >> 16) & 255, (nb >> 16) & 255));
  const g = cl(mix((na >> 8) & 255, (nb >> 8) & 255));
  const bl = cl(mix(na & 255, nb & 255));
  return "#" + ((r << 16) | (g << 8) | bl).toString(16).padStart(6, "0");
}
